import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { SessionService } from '../session/session.service';

/**
 * Gate a route on the ACTING user being a platform Super Admin.
 *
 *   {
 *     path: 'admin/clients',
 *     canActivate: [superAdminGuard],
 *     loadComponent: () => import('./features/admin/clients-page.component').then(m => m.ClientsPageComponent),
 *   }
 *
 * A third gate alongside `featureGuard` (what the CLIENT has) and the permission checks (what this
 * USER may do), for the small set of screens that decide who may act as which client. Those cannot be
 * permissions: a permission is grantable, and an administrator who could grant themselves membership
 * of another tenant would be escalating across the boundary the multi-client model rests on.
 *
 * Hiding the nav item is not enough on its own — a bookmark or a typed URL lands on the route
 * directly. UI routing only; the server refuses the endpoints regardless ([SuperAdminOnly]).
 *
 * Reads `isSuperAdmin`, not `isRealSuperAdmin`, so it follows impersonation: a Super Admin
 * impersonating an ordinary user is turned away here exactly as the API turns them away, and the two
 * agree rather than rendering a screen whose every call 403s.
 */
export const superAdminGuard: CanActivateFn = () => {
  const session = inject(SessionService);
  const router = inject(Router);

  if (session.isSuperAdmin()) {
    return true;
  }

  // Redirect rather than render a "no access" page, matching featureGuard: there is nothing to
  // explain and nothing the user can request, so the honest behaviour is for the URL not to resolve.
  return router.createUrlTree(['/people']);
};
